// v2.1 finding #54 (Phase 4 rehearsal — Biomass form refresh-discards-draft):
// Pure predicates that decide whether one of the three Biomass entry forms
// (Sampling / Count / Harvest) carries any user-typed input that would be
// destroyed by a page refresh, tab close, or accidental Chrome Android
// pull-to-refresh swipe. Used to gate a single `beforeunload` warning on the
// Biomass screen — the cheap defensive layer that v2.1 ships. The full
// debounce-write draft-persistence pattern (option B in the finding) is
// parked for v2.2.
//
// T3-D-04a (2026-07-10): removed `stage` from every draft field type. The
// Biomass log panels no longer expose an operator-facing stage dropdown —
// the row's stage is snapshot-derived from `Lot.stage` at save time via
// `toProductionRowStage`. See docs/architecture-concerns.md T3-D-04 sub-a.
//
// T3-C-01 (2026-07-13): removed `stockCount` from `SamplingDraftFields`.
// The Muestreo / Sampling panel no longer accepts fish-stock input;
// operators enter fish-stock via the Conteo/Estimación panel which writes
// a `FishStockLog` (T3-A-03 bridge). See T3-C-01 in the plan.

export type SamplingDraftFields = Readonly<{
  lotId: string
  averageWeightG: string
  dailyMortality: string
  notes: string
}>

export type CountDraftFields = Readonly<{
  lotId: string
  method: 'estimate' | 'exact'
  stockCount: string
  notes: string
}>

export type HarvestDraftFields = Readonly<{
  lotId: string
  harvestKg: string
  harvestCount: string
  notes: string
}>

// `recordedAt` is intentionally excluded — it is initialized to `now` at
// mount and would always look "different from empty" without any user
// interaction. The dirty signal must reflect operator-typed values only.

const hasContent = (value: string): boolean => value.trim().length > 0

export const isSamplingDraftDirty = (draft: SamplingDraftFields): boolean => {
  if (hasContent(draft.lotId)) return true
  if (hasContent(draft.averageWeightG)) return true
  if (hasContent(draft.dailyMortality)) return true
  if (hasContent(draft.notes)) return true
  return false
}

export const isCountDraftDirty = (draft: CountDraftFields): boolean => {
  if (hasContent(draft.lotId)) return true
  if (hasContent(draft.stockCount)) return true
  if (hasContent(draft.notes)) return true
  if (draft.method !== 'estimate') return true
  return false
}

export const isHarvestDraftDirty = (draft: HarvestDraftFields): boolean => {
  if (hasContent(draft.lotId)) return true
  if (hasContent(draft.harvestKg)) return true
  if (hasContent(draft.harvestCount)) return true
  if (hasContent(draft.notes)) return true
  return false
}

export const isAnyBiomassDraftDirty = (args: Readonly<{
  sampling: SamplingDraftFields
  count: CountDraftFields
  harvest: HarvestDraftFields
}>): boolean =>
  isSamplingDraftDirty(args.sampling)
  || isCountDraftDirty(args.count)
  || isHarvestDraftDirty(args.harvest)
